/**
 * @file OrderChainStepperDots.tsx
 * @description Примитивы шкалы стадий: отрезок между точками и сама точка
 * (phase19.24 §14; вынесено из OrderChainStepper ради лимита 200 строк).
 *
 * Цвет точки отвечает на один вопрос — чей ход. Точка-заглушка маршрута
 * («Получение», способ получения не выбран) рисуется серой: обещать ход
 * склада или клиента по неизвестной точке нельзя.
 */

import type { OrderRouteStageDto } from "../../../lib/order-chain";
import { ownerMeta } from "../../../lib/order-chain-owner";
import { cn } from "@/lib/utils";
/** Отрезок между точками: пройденный, «к вашему шагу» (зелёный пунктир) или будущий. */
export function Connector({
  visible,
  passed,
  toMine,
}: {
  visible: boolean;
  passed: boolean;
  toMine: boolean;
}) {
  return (
    <span
      aria-hidden
      className={cn(
        "h-px flex-1",
        !visible && "opacity-0",
        passed
          ? "bg-primary/40"
          : toMine
            ? "border-t border-dashed border-primary/60"
            : "bg-on-surface/12",
      )}
    />
  );
}

/** Точка стадии: пройдена (залита), текущая (кольцо), будущая (контур). */
export function Dot({
  stage,
  isCurrent,
  isNextMine,
  placeholder,
}: {
  stage: OrderRouteStageDto;
  isCurrent: boolean;
  isNextMine: boolean;
  /** Точка-заглушка маршрута: серая, без цвета владельца. */
  placeholder: boolean;
}) {
  const meta = ownerMeta(placeholder ? "SYSTEM" : stage.owner);
  const passed = stage.state === "passed";
  const skipped = stage.state === "skipped" || placeholder;
  return (
    <span
      aria-hidden
      className={cn(
        "size-3 shrink-0 rounded-full",
        skipped && "bg-on-surface/12",
        passed && !skipped && "bg-primary/60",
        !passed && !skipped && !isCurrent && meta.outline,
        isCurrent && cn(meta.dot, "ring-4 ring-primary/20"),
        // Одно мягкое расширение кольца при первой отрисовке «вашего хода»;
        // `motion-safe` выключает его при prefers-reduced-motion.
        isCurrent &&
          stage.owner === "MANAGER" &&
          "motion-safe:animate-in motion-safe:zoom-in-50 motion-safe:duration-700",
        isNextMine && "ring-2 ring-primary",
      )}
    />
  );
}

